import React from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, FormHelperText, Grid, TextField } from "@mui/material";

import moment from "moment";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import FileDropzone from "components/Shared/FileDropzone";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";

import { SHIPMENT_FD_STATUS } from "data/static/staticOptions";

import { useAuth, useDOUploadFile } from "hooks";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const INITIAL_STATE = {
  account: "",
  entity: "",
  type: "",
  shipmentId: "",
  issueDate: "",
  expirationDate: "",
  attachments: [],
  isActive: true,
};
const ShipmentFulfillmentDocumentForm = ({
  isBusy = false,
  shipmentFDDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();

  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const { handleUploadFiles } = useDOUploadFile();

  // Reactive Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [fetchShipment, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentListQuery();

  // methods
  const handleOnShipmentFDSubmit = async (data) => {
    const _shipmentFDPayLoad = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      shipmentId: data?.shipmentId?._id,
    };

    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (!!newAttachments?.length) {
      const { uploadedFiles } = await handleUploadFiles(
        newAttachments,

        {
          folderName: "SHIPMENT_DOCUMENT",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        }
      );

      _shipmentFDPayLoad.attachments = [
        ...existingAttachments,
        ...uploadedFiles,
      ];
    }
    onSubmit(_shipmentFDPayLoad);
  };
  const { data: accountTypeList = [] } = useListListQuery({
    type: ENTITY_LIST_CONSTANTS.DOCUMENT_TYPES,
    status: "Active",
  });
  const _mappedAccountTypeList = Array.isArray(accountTypeList)
    ? accountTypeList.map((item) => ({
      label: item?.name,
      id: item?._id,
    }))
    : [];
  React.useEffect(() => {
    if (!shipmentFDDetail) return;
    reset({
      ...shipmentFDDetail,
      ...(shipmentFDDetail?.issueDate && {
        issueDate: moment(shipmentFDDetail.issueDate).format("YYYY-MM-DD"),
      }),
      ...(shipmentFDDetail?.expirationDate && {
        expirationDate: moment(shipmentFDDetail.expirationDate).format(
          "YYYY-MM-DD"
        ),
      }),
    });
  }, [shipmentFDDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id,
      });
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchShipment({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipment, viewEntity]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicleId", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  disabled={!!shipmentFDDetail}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Type is required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Type"
                  options={
                    _mappedAccountTypeList?.map((option) => option.label) ?? []
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="issueDate"
              control={control}
              rules={{ required: "Issue Date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Issue Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="expirationDate"
              control={control}
              rules={{ required: "Expiration Date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Expiration Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Status is required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Status"
                  value={field.value || ""}
                  options={SHIPMENT_FD_STATUS}
                />
              )}
            />
          </Grid>

          <Grid item md={12}>
            <FormHelperText>Attachments</FormHelperText>

            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => setValue("attachments", files)}
                />
              )}
            />
          </Grid>
          {shipmentFDDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnShipmentFDSubmit)}
        >
          {shipmentFDDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentFulfillmentDocumentForm;
